import { useContext, useEffect, useState, type FC } from "react";
import MainLayout from "../components/MainLayout";
import { Button, Checkbox, IconButton } from "rsuite";
import PlusIcon from "@rsuite/icons/Plus";
import { SearchContext } from "../contexts/SearchContext";
import { LoadingContext } from "../contexts/LoadingContext";
import { PaginationResponse } from "../objects/pagination";
import { ProductCategory } from "../models/inventory/product_category";
import {
  createProductCategory,
  deleteProductCategory,
  getProductCategories,
  updateProductCategory,
} from "../services/inventory/ProductCategoryApi";
import { getPagination, noImage } from "../utils/helper";
import { errorToast } from "../utils/helper-ui";
import { Table, Pagination } from "rsuite";
import { BsTrash3 } from "react-icons/bs";
import ProductCategoryForm from "../components/features/inventory/ProductCategoryForm";
const { Column, HeaderCell, Cell } = Table;
interface ProductCategoryPageProps {}

const ProductCategoryPage: FC<ProductCategoryPageProps> = ({}) => {
  const { search, setSearch } = useContext(SearchContext);
  const { isLoading, setIsLoading } = useContext(LoadingContext);
  const [mounted, setMounted] = useState(false);
  const [page, setPage] = useState(1);
  const [size, setSize] = useState(30);
  const [pagination, setPagination] = useState<PaginationResponse>();
  const [productCategories, setProductCategories] = useState<ProductCategory[]>(
    []
  );
  const [modalFormOpen, setModalFormOpen] = useState(false);
  const [selectedProductCategory, setSelectedProductCategory] =
    useState<ProductCategory>();

  const [checkedKeys, setCheckedKeys] = useState<string[]>([]);
  let checked = false;
  let indeterminate = false;

  if (checkedKeys.length === productCategories.length) {
    checked = true;
  } else if (checkedKeys.length === 0) {
    checked = false;
  } else if (
    checkedKeys.length > 0 &&
    checkedKeys.length < productCategories.length
  ) {
    indeterminate = true;
  }

  const handleCheckAll = (
    value: any,
    checked: boolean,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const keys = checked ? productCategories.map((item) => item.id) : [];
    setCheckedKeys(keys);
  };
  const handleCheck = (
    value: any,
    checked: boolean,
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const keys = checked
      ? [...checkedKeys, value as string]
      : checkedKeys.filter((item) => item !== (value as string));
    setCheckedKeys(keys);
  };

  useEffect(() => {
    setMounted(true);

    return () => {
      setMounted(false);
    };
  }, []);

  useEffect(() => {
    if (!mounted) return;
    fetchData();
  }, [mounted, search, page, size]);

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const res = await getProductCategories({ page, size, search });
      const resJson = await res.json();
      //   console.log(data);
      setProductCategories((prev) => [...prev, ...resJson.data.items]);
      setPagination(getPagination(resJson.data));
    } catch (error) {
      errorToast(`${error}`);
    } finally {
      setIsLoading(false);
    }
  };

  const removeProductCategory = async (productCategory: ProductCategory) => {
    try {
      setIsLoading(true);
      if (
        window.confirm(
          `Are you sure you want to delete productCategory with name ${productCategory.name}?`
        )
      ) {
        await deleteProductCategory(productCategory.id!);
        fetchData();
      }
    } catch (error) {
      errorToast(`${error}`);
    } finally {
      setIsLoading(false);
    }
  };

  const saveProductCategory = async (productCategory: ProductCategory) => {
    try {
      setIsLoading(true);
      if (selectedProductCategory) {
        await updateProductCategory(
          selectedProductCategory.id!,
          productCategory
        );
      } else {
        await createProductCategory(productCategory);
      }
      setModalFormOpen(false);
      setSelectedProductCategory(undefined);
      fetchData();
      setProductCategories([]);
    } catch (error) {
      errorToast(`${error}`);
    } finally {
      setIsLoading(false);
    }
  };
  const renderHeader = () => (
    <div className="flex flex-row justify-between items-center px-2">
      <h1 className="text-2xl font-semibold">Product Category</h1>
      {/* button */}
      <div>
        <IconButton
          size="sm"
          appearance="primary"
          color="green"
          icon={<PlusIcon />}
          onClick={() => setModalFormOpen(true)}
        >
          Create Product Category
        </IconButton>
      </div>
    </div>
  );
  return (
    <MainLayout header={renderHeader()}>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr>
              <th
                className="px-4 py-2 text-left border bg-gray-50"
                style={{ width: 100 }}
              >
                <Checkbox
                  inline
                  checked={checked}
                  indeterminate={indeterminate}
                  onChange={handleCheckAll}
                />
              </th>
              <th className="px-4 py-2 text-left border bg-gray-50">Name</th>
              <th className="px-4 py-2 text-left border bg-gray-50">
                Description
              </th>
              <th className="px-4 py-2 text-left border bg-gray-50">Icon</th>
              <th className="px-4 py-2 text-left border bg-gray-50">Action</th>
            </tr>
          </thead>
          <tbody>
            {productCategories.map((item, i) => (
              <tr key={item.id}>
                <td className="px-4 py-2 border">
                  <Checkbox
                    value={item.id}
                    inline
                    onChange={handleCheck}
                    checked={checkedKeys.some((e) => e === item.id)}
                  />
                  {i + 1}
                </td>

                <td className="px-4 py-2 border">
                  <div
                    className="hover:underline cursor-pointer font-semibold"
                    onClick={() => {
                      setSelectedProductCategory(item);
                      setModalFormOpen(true);
                    }}
                  >
                    {item.name}
                  </div>
                </td>
                <td className="px-4 py-2 border">{item.description}</td>
                <td className="px-4 py-2 border">
                  <div
                    className="flex justify-center items-center hover:underline cursor-pointer font-semibold"
                    onClick={() => {}}
                  >
                    <img
                      onError={noImage}
                      src={item.icon_url}
                      width="20"
                      height="20"
                    />
                  </div>
                </td>
                <td className="px-4 py-2 border">
                  <BsTrash3
                    className="text-red-500 hover:text-red-800"
                    size={16}
                    onClick={() => removeProductCategory(item)}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {!pagination?.last && (
        <div className="flex justify-center my-2">
          <Button
            onClick={() => {
              setPage((pagination?.page ?? 0) + 1);
            }}
          >
            Load more
          </Button>
        </div>
      )}
      {/* <Table
        minHeight={700}
        data={productCategories}
        onRowClick={(data, i) => {}}
      >
        <Column width={50} align="center" fixed>
          <HeaderCell style={{ padding: 0 }}>
            <div style={{ lineHeight: "40px" }}>
              <Checkbox
                inline
                checked={checked}
                indeterminate={indeterminate}
                onChange={handleCheckAll}
              />
            </div>
          </HeaderCell>
          <Cell style={{ padding: 0 }}>
            {(item) => (
              <div style={{ lineHeight: "46px" }}>
                <Checkbox
                  value={item.id}
                  inline
                  onChange={handleCheck}
                  checked={checkedKeys.some((e) => e === item.id)}
                />
              </div>
            )}
          </Cell>
        </Column>
        <Column width={70} align="center" fixed>
          <HeaderCell>No</HeaderCell>
          <Cell>
            {(item) => (
              <div>
                {(page - 1) * size +
                  productCategories.indexOf(item as ProductCategory) +
                  1}
              </div>
            )}
          </Cell>
        </Column>

        <Column width={200} resizable fixed>
          <HeaderCell>Name</HeaderCell>
          <Cell>
            {(item) => (
              <div
                className="hover:underline cursor-pointer font-semibold "
                onClick={() => {
                  setSelectedProductCategory(item as ProductCategory);
                  setModalFormOpen(true);
                }}
              >
                {item.name}
              </div>
            )}
          </Cell>
        </Column>

        <Column width={200} resizable>
          <HeaderCell>Description</HeaderCell>
          <Cell dataKey="description" />
        </Column>

        <Column width={200} resizable>
          <HeaderCell>Action</HeaderCell>
          <Cell>
            {(item) => (
              <BsTrash3
                className="text-red-500 hover:text-red-800"
                size={16}
                onClick={() =>
                  removeProductCategory(item as ProductCategory)
                }
              />
            )}
          </Cell>
        </Column>
        <Column width={200} resizable fixed>
          <HeaderCell>Icon</HeaderCell>
          <Cell style={{ padding: 0 }}>
            {(item) => (
              <div
                className="flex justify-center items-center hover:underline cursor-pointer font-semibold w-10 h-10 "
                style={{ backgroundColor: item.color }}
                onClick={() => {
                  
                }}
              >
                <img onError={noImage} src={item.icon_url} width={"20"} height={"20"} />
              </div>
            )}
          </Cell>
        </Column>
      </Table> */}
      {/* <div style={{ padding: 20 }}>
        <Pagination
          prev
          next
          first
          last
          ellipsis
          boundaryLinks
          maxButtons={5}
          size="xs"
          layout={["total", "-", "limit", "|", "pager", "skip"]}
          total={pagination?.total ?? 0}
          limitOptions={[10, 30, 50]}
          limit={pagination?.size}
          activePage={pagination?.page}
          onChangePage={setPage}
          onChangeLimit={setSize}
        />
      </div> */}
      <ProductCategoryForm
        productCategory={selectedProductCategory}
        open={modalFormOpen}
        onClose={() => {
          setModalFormOpen(false);
          setSelectedProductCategory(undefined);
        }}
        onSubmit={saveProductCategory}
      />
    </MainLayout>
  );
};
export default ProductCategoryPage;
